import React, { memo, useState, useCallback } from "react";
import { List, Popover } from "antd";
import { Assets } from "../../../constants/images";
import AvatarImage from "../../Common/AvatarImage";
import ChatProfileHover from "../../Common/ChatProfileHover";
import { ReactionDisplay, ReactionPicker } from "./Reactions";
import { message_type } from "../../../utils/constants";
import { plainChatText } from "../../../utils/chatFormatting";
import { ReplyIcon } from "../../../constants/svg";

const getReplyMediaLabel = (replyMessageType) => {
  if (replyMessageType == message_type?.image) return "Photo";
  if (replyMessageType == message_type?.video) return "Video";
  if (replyMessageType == message_type?.gif) return "GIF";
  if (replyMessageType == message_type?.pdf) return "Document";
  if (replyMessageType == message_type?.media) return "Document";
  if (replyMessageType == message_type?.media_collection) return "Media";
  if (replyMessageType == message_type?.audio) return "Audio";
  if (replyMessageType == message_type?.voice_memo) return "Voice Memo";
  return null;
};

// A single, memoized message row. Renders the day separator (when
// `showDateSeparator` is true), the bubble itself, reactions, and the hover
// reaction picker. Owns its own hover state so one hovered bubble does not
// cause the others to re-render.
const MessageBubble = memo(function MessageBubble({
  item,
  isLastItem,
  isGroupChat,
  chatId,
  isSentByAdmin,
  showDateSeparator,
  dateLabel,
  messageTimestamp,
  userData,
  senderFullInfo,
  sender,
  authUser,
  messagesEndRef,
  renderMessageContent,
  onItemClick,
  onUsernameClick,
  onReactionUserClick,
  onDeleteMessage,
  onEditMessage,
  onReply,
  onSeenBy,
  scrollToBottom,
  isUserNearBottomRef,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const closePicker = useCallback(() => setIsHovered(false), []);
  const handleItemClick = useCallback(
    () => onItemClick?.(senderFullInfo),
    [onItemClick, senderFullInfo],
  );
  const handleUsernameClick = useCallback(
    (e) => onUsernameClick?.(e, item?.sender_id),
    [onUsernameClick, item?.sender_id],
  );

  if (item?.message_type == message_type?.member_leave) {
    return (
      <>
        <span className="message-item-timestamp">{item.text}</span>
        {isLastItem && <div ref={messagesEndRef} />}
      </>
    );
  }

  const messageClassName = isSentByAdmin ? "admin-message" : "customer-message";

  const reactionBlock = (
    <>
      <ReactionDisplay
        message={item}
        chatId={chatId}
        onReactionUserClick={onReactionUserClick}
      />
      {isHovered && (
        <ReactionPicker
          scrollToBottom={scrollToBottom}
          isUserNearBottomRef={isUserNearBottomRef?.current}
          visible={true}
          onClose={closePicker}
          message={item}
          chatId={chatId}
          onDeleteMessage={onDeleteMessage}
          onEditMessage={onEditMessage}
          onReply={onReply}
          onSeenBy={onSeenBy}
          isLastMessage={isLastItem}
        />
      )}
    </>
  );

  const reply = item?.reply;
  const hasReply = Boolean(
    reply &&
      (reply.message_id || reply.text || reply.message_type != null),
  );
  const replyMediaLabel = hasReply
    ? getReplyMediaLabel(reply?.message_type)
    : null;
  const replySnippet =
    replyMediaLabel ||
    (reply?.text && reply.text.trim().length > 0
      ? plainChatText(reply.text)
      : "Message");

  const handleReplyQuoteClick = useCallback(
    (e) => {
      e.stopPropagation();
      const targetId = reply?.message_id;
      if (!targetId) return;
      const el = document.querySelector(`[data-thread-id="${targetId}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    [reply?.message_id],
  );

  const replyQuote = hasReply ? (
    <div
      className="message-bubble__reply-quote"
      onClick={handleReplyQuoteClick}
      role={reply?.message_id ? "button" : undefined}
    >
      <span className="message-bubble__reply-quote-icon">
        <ReplyIcon width={18} height={14} color="#6f42c1" />
      </span>
      <span className="message-bubble__reply-quote-snippet">
        {replySnippet}
      </span>
    </div>
  ) : null;

  const bubbleBody = (
    <div className="messageTimeWrapper d-flex">
      <div className="d-flex">
        <div className="user-name" onClick={handleUsernameClick}>
          {userData?.name}
        </div>
        <span className="user-message-item-timestamp">{messageTimestamp}</span>
      </div>
      {item?.message_type == message_type?.text ? (
        <div className="message mx-3">
          <div className="message-content">{renderMessageContent(item)}</div>
          {replyQuote}
          {reactionBlock}
        </div>
      ) : (
        <>
          {renderMessageContent(item)}
          {replyQuote}
          {reactionBlock}
        </>
      )}
    </div>
  );

  return (
    <>
      {showDateSeparator ? (
        <span className="message-item-timestamp">{dateLabel}</span>
      ) : null}
      <List.Item
        className={`${messageClassName} message-hover`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: "relative" }}
        data-thread-id={item?.thread_id || undefined}
      >
        {!isSentByAdmin &&
          (isGroupChat ? (
            <div className="chatAvatatWithStatus smallSize">
              {!userData?.isDeleted ? (
                <Popover
                  placement="rightTop"
                  content={
                    <ChatProfileHover
                      user={sender}
                      userPosition={[]}
                      onItemClick={handleItemClick}
                      users={[]}
                      username={userData?.name}
                      avatar={userData?.thumbnail ?? userData?.image}
                    />
                  }
                  arrow={true}
                  key={item?.id}
                >
                  <div>
                    <AvatarImage
                      src={userData?.thumbnail ?? userData?.image}
                      name={userData?.name}
                      placeholder={Assets.ProfilePlaceholder}
                      size={40}
                    />
                  </div>
                </Popover>
              ) : (
                <div>
                  <AvatarImage
                    src={userData?.thumbnail ?? userData?.image}
                    name={userData?.name}
                    placeholder={Assets.ProfilePlaceholder}
                    size={40}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="chatAvatatWithStatus smallSize">
              <AvatarImage
                name={userData?.name}
                src={userData?.thumbnail ?? userData?.image}
                placeholder={Assets.ProfilePlaceholder}
                size={40}
              />
            </div>
          ))}

        {bubbleBody}

        {isSentByAdmin &&
          (isGroupChat ? (
            <AvatarImage
              src={userData?.thumbnail ?? userData?.image}
              name={userData?.name}
              placeholder={Assets.ProfilePlaceholder}
              size={40}
            />
          ) : (
            <AvatarImage
              name={authUser?.name}
              src={authUser?.thumbnail ?? authUser?.image}
              placeholder={Assets.ProfilePlaceholder}
              size={40}
            />
          ))}
      </List.Item>
      {isLastItem && <div ref={messagesEndRef} />}
    </>
  );
});

export default MessageBubble;
